
import { useMemo } from "react";
import { MetricCard } from "./enhanced/MetricCard";
import { Users, Target, TrendingUp, Clock } from "lucide-react";
import type { Candidate } from "@/components/candidates/types";

interface RecruitmentKPIsProps {
  candidates: Candidate[];
}

export const RecruitmentKPIs = ({ candidates }: RecruitmentKPIsProps) => {
  const metrics = useMemo(() => {
    if (!candidates || candidates.length === 0) {
      return {
        totalCandidates: 0,
        hiredCount: 0,
        conversionRate: 0,
        averageTimeToHire: 0,
        activeCount: 0
      };
    }

    const totalCandidates = candidates.length;
    const hiredCandidates = candidates.filter(c => c.status === 'hired');
    const hiredCount = hiredCandidates.length;
    const conversionRate = totalCandidates > 0 ? (hiredCount / totalCandidates) * 100 : 0;
    
    // Calculate average time to hire for hired candidates
    const averageTimeToHire = hiredCandidates.length > 0 
      ? hiredCandidates.reduce((sum, candidate) => {
          if (!candidate.created_at || !candidate.accepted_date) return sum;
          const startDate = new Date(candidate.created_at);
          const endDate = new Date(candidate.accepted_date);
          const timeDiff = endDate.getTime() - startDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          return sum + Math.max(0, daysDiff);
        }, 0) / hiredCandidates.length
      : 0;

    const activeCount = candidates.filter(c => 
      ['new', 'contacted', 'interview', 'offer'].includes(c.status || '')
    ).length;

    return {
      totalCandidates,
      hiredCount,
      conversionRate,
      averageTimeToHire: Math.round(averageTimeToHire),
      activeCount
    };
  }, [candidates]);

  const getConversionTrend = () => {
    if (metrics.conversionRate >= 15) return "up";
    if (metrics.conversionRate >= 5) return "neutral";
    return "down";
  };

  const getTimeToHireTrend = () => {
    if (metrics.averageTimeToHire <= 14) return "up";
    if (metrics.averageTimeToHire <= 30) return "neutral";
    return "down";
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Total Candidates"
        value={metrics.totalCandidates.toString()}
        icon={<Users className="h-4 w-4" />}
        description="All candidates in pipeline"
        trend="neutral"
      />
      
      <MetricCard
        title="Successful Hires"
        value={metrics.hiredCount.toString()}
        icon={<Target className="h-4 w-4" />}
        description="Candidates successfully placed"
        trend={metrics.hiredCount > 0 ? "up" : "neutral"}
      />
      
      <MetricCard
        title="Conversion Rate"
        value={`${metrics.conversionRate.toFixed(1)}%`}
        icon={<TrendingUp className="h-4 w-4" />}
        description="Hired vs total candidates"
        trend={getConversionTrend()}
      />
      
      <MetricCard
        title="Avg Time to Hire"
        value={metrics.averageTimeToHire > 0 ? `${metrics.averageTimeToHire}d` : "N/A"}
        icon={<Clock className="h-4 w-4" />}
        description="Days from application to hire"
        trend={getTimeToHireTrend()}
      />
    </div>
  );
};
